import { useQuery } from 'react-query';

import { useRequest, type QueryFunction, queryClient } from '@frontend/api';

import type {
  QuoteFilesListResponseBody,
  QuoteFilesListRequestBody,
} from './types';

export const allQuoteFilesListKey = ['quotes', 'quoteFiles', 'list'] as const;

type ListQuoteFilesQueryKey = readonly [
  ...typeof allQuoteFilesListKey,
  QuoteFilesListRequestBody,
];

export const getListQuoteFilesQueryKey = (
  param: QuoteFilesListRequestBody,
): ListQuoteFilesQueryKey => [...allQuoteFilesListKey, param];

export const invalidateQuoteFilesQuery = (quoteId?: number | null) => {
  if (quoteId) {
    queryClient.invalidateQueries(getListQuoteFilesQueryKey({ quoteId }));
  }
};

export const invalidateAllQuoteFileQueries = () => {
  queryClient.invalidateQueries(allQuoteFilesListKey);
};

export const useListQuoteFilesQuery: QueryFunction<
  QuoteFilesListResponseBody,
  [QuoteFilesListRequestBody],
  ListQuoteFilesQueryKey
> = (param, { requestOptions, queryOptions } = {}) => {
  const { quoteId } = param;
  const { enabled: enabledOption, ...restQueryOptions } = queryOptions ?? {};
  const enabled = quoteId === null ? false : enabledOption;
  const queryKey = getListQuoteFilesQueryKey(param);
  const request = useRequest(requestOptions);

  return useQuery({
    ...restQueryOptions,
    enabled,
    queryKey,
    queryFn: ({ signal }) =>
      request(`/quotes/${param.quoteId}/files/`, {
        init: { method: 'GET', signal },
        query: { search: param.search, page: param.page },
      }),
  });
};
